import { useQuery } from "@apollo/client";
import Dialog from "components/generic/Dialog";
import gql from "graphql-tag";
import React, { useState } from "react";

import {
  BulkImportRequestFragmentFragment,
  BulkImportRowFragmentFragment,
} from "~/components/Admin/User/BulkImport/BulkImport.generated";
import { useBulkImportWizard } from "~/components/Admin/User/BulkImport/hooks/useBulkImportWizard";
import { DoneStep } from "~/components/Admin/User/BulkImport/steps/DoneStep";
import { ReviewStep } from "~/components/Admin/User/BulkImport/steps/ReviewStep";
import { SendInvitationsStep } from "~/components/Admin/User/BulkImport/steps/SendInvitationsStep";
import { UploadStep } from "~/components/Admin/User/BulkImport/steps/UploadStep";
import { ValidateErrorsStep } from "~/components/Admin/User/BulkImport/steps/ValidateErrorsStep";
import LoadingSpinner from "~/components/generic/LoadingSpinner";

const ROLES_QUERY = gql`
  query BulkImportRoles($orgSlug: String!) {
    roles(orgSlug: $orgSlug) {
      id
      name
    }
  }
`;

interface Props {
  orgSlug: string;
  visible: boolean;
  close: () => void;
  onOpenQaDialog?: (groupNames: string[]) => void;
  resumeRequestId?: string | null;
}

const UsersIcon = () => (
  <svg
    width="22"
    height="17"
    viewBox="0 0 22 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ verticalAlign: "middle", marginBottom: 4 }}
  >
    {/* left/back user */}
    <path
      fillOpacity="0.55"
      fill="#008CD8"
      fillRule="evenodd"
      d="
      M5.5 7C6.88 7 8 5.88 8 4.5V3C8 1.62 6.88 0.5 5.5 0.5C4.12 0.5 3 1.62 3 3V4.5C3 5.88 4.12 7 5.5 7Z
      M5.5 8C2.74 8 0.5 9.79 0.5 12.5C0.5 13.33 1.17 14 2 14H9C9.83 14 10.5 13.33 10.5 12.5C10.5 9.79 8.26 8 5.5 8Z
    "
    />
    {/* right/front user */}
    <path
      fill="#008CD8"
      fillRule="evenodd"
      d="
      M16.5 7C17.88 7 19 5.88 19 4.5V3C19 1.62 17.88 0.5 16.5 0.5C15.12 0.5 14 1.62 14 3V4.5C14 5.88 15.12 7 16.5 7Z
      M16.5 8C13.74 8 11.5 9.79 11.5 12.5C11.5 13.33 12.17 14 13 14H20C20.83 14 21.5 13.33 21.5 12.5C21.5 9.79 19.26 8 16.5 8Z
    "
    />
  </svg>
);

export const BulkImportDialog = ({
  orgSlug,
  visible,
  close,
  onOpenQaDialog,
  resumeRequestId,
}: Props) => {
  const wizard = useBulkImportWizard(resumeRequestId);
  const [request, setRequest] =
    useState<BulkImportRequestFragmentFragment | null>(null);

  const { data: rolesData, loading: rolesLoading } = useQuery(ROLES_QUERY, {
    variables: { orgSlug },
    skip: !visible,
  });

  const roles: Array<{ id: string; name: string }> = rolesData?.roles ?? [];

  const handleUploadSuccess = (
    requestId: string,
    req: BulkImportRequestFragmentFragment
  ) => {
    setRequest(req);
    wizard.setRequestId(requestId);
    wizard.setStep("REVIEW");
  };

  const handleValidationErrors = (
    errors: Array<{
      sheet: string;
      row?: number | null;
      column?: string | null;
      message: string;
    }>
  ) => {
    wizard.setValidationErrors(errors);
    wizard.setStep("VALIDATE_ERRORS");
  };

  const handleInvitationsSent = (
    updatedRequest: BulkImportRequestFragmentFragment
  ) => {
    setRequest(updatedRequest);
    wizard.setStep("DONE");
  };

  const handleOpenQa = () => {
    if (onOpenQaDialog && request) {
      const groupNames = [
        ...new Set(
          request.rows
            .map((r: BulkImportRowFragmentFragment) => r.userGroupName)
            .filter(Boolean) as string[]
        ),
      ];
      onOpenQaDialog(groupNames);
    }
    close();
  };

  return (
    <Dialog
      header={
        <span>
          <UsersIcon />
          <span style={{ paddingLeft: 10 }}>Import Users</span>
        </span>
      }
      isOpen={visible}
      onRequestClose={close}
      style={{ content: { width: 820, maxWidth: "95vw" } }}
    >
      {rolesLoading && wizard.step === "UPLOAD" ? <LoadingSpinner /> : null}

      {wizard.step === "UPLOAD" && !rolesLoading && (
        <UploadStep
          orgSlug={orgSlug}
          roles={roles}
          onSuccess={handleUploadSuccess}
          onValidationErrors={handleValidationErrors}
          onCancel={close}
        />
      )}
      {wizard.step === "VALIDATE_ERRORS" && (
        <ValidateErrorsStep
          errors={wizard.validationErrors}
          onBack={() => wizard.setStep("UPLOAD")}
        />
      )}
      {wizard.step === "REVIEW" && request && (
        <ReviewStep
          orgSlug={orgSlug}
          request={request}
          onConfirmed={(updatedRequest) => {
            setRequest(updatedRequest);
            updatedRequest.rowCounts.newUser > 0
              ? wizard.setStep("SEND_INVITATIONS")
              : wizard.setStep("DONE");
          }}
        />
      )}
      {wizard.step === "SEND_INVITATIONS" && request && (
        <SendInvitationsStep
          request={request}
          onSent={handleInvitationsSent}
          onSkip={() => wizard.setStep("DONE")}
        />
      )}
      {wizard.step === "DONE" && request && (
        <DoneStep
          orgSlug={orgSlug}
          request={request}
          onOpenQaDialog={onOpenQaDialog ? handleOpenQa : undefined}
          onClose={close}
        />
      )}
    </Dialog>
  );
};
